import axios, { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { Capacitor } from '@capacitor/core';
import { storage, STORAGE_KEYS } from '../platform/storage';
import { getOAuthTenant } from '../auth/oauthStorage';
import {
  getStoredRefreshToken,
  storeRefreshTokenIfNative,
} from '../auth/refreshTokenStorage';
import type { ApiError } from './types';

/**
 * Per-app configuration for the shared axios instance. Each frontend
 * (Dispatch field worker, customer portal) wires this once at startup with
 * its own toast, routing, and limit-handling seams.
 */
export interface ApiClientConfig {
  /** API base URL, e.g. https://api.packetprospector.com/api */
  baseUrl: string;
  /** Bridge to the app's toast / snackbar layer (react-hot-toast, MUI Snackbar, etc.) */
  notify: {
    error(message: string): void;
  };
  /** App-defined route fragments. The shared client builds redirects from these. */
  routes: {
    /** Path to the login page, e.g. "/login" */
    login: string;
    /** Path to the "tenant not found" landing, e.g. "/tenant-not-found" */
    tenantNotFound: string;
    /**
     * Paths where session-expired / session-timeout redirects should NOT fire.
     * Typically: login, password reset, forgot password, the root, etc.
     */
    publicPaths: string[];
    /**
     * Optional path-prefix allow-list. Returning true for the current path
     * suppresses the auto-redirect on auth failures. Used by Dispatch for
     * its tenant-subdomain marketing landing under /service-areas/.
     */
    skipRedirectForPath?: (pathname: string) => boolean;
  };
  /**
   * Return true if the current location is a public registration route where
   * the tenant header should not be attached and the tenant-not-found
   * redirect should be suppressed. Dispatch returns true on /register-isp and
   * /register/{success,canceled}; the customer portal returns false always.
   */
  isPublicRegistrationRoute: () => boolean;
  /**
   * Optional handler for 400 LIMIT_EXCEEDED responses. Dispatch wires this
   * to a global "you've hit a plan limit" modal; the customer portal can
   * omit it.
   */
  onLimitExceeded?: (error: { limitType: string; message: string }) => void;
  /** A 403 whose body says the tenant's plan lacks the module the endpoint belongs to */
  onModuleNotIncluded?: (error: { module: string; message: string }) => void;
}

/**
 * Create an axios instance wired with PacketProspector's request/response
 * conventions: bearer token + tenant subdomain + native-shell refresh-token
 * headers on the request side, transparent token refresh + redirect-on-401
 * + LIMIT_EXCEEDED bridge on the response side.
 *
 * Call once per app at module init and export the returned instance.
 */
export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const apiClient: AxiosInstance = axios.create({
    baseURL: config.baseUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    // SECURITY: Required to send/receive HttpOnly cookies cross-origin.
    withCredentials: true,
  });

  apiClient.interceptors.request.use(
    async (request: InternalAxiosRequestConfig) => {
      const token = storage.get<string | null>(STORAGE_KEYS.JWT_TOKEN, null);
      if (token && request.headers) {
        request.headers.Authorization = `Bearer ${token}`;
      }

      if (Capacitor.isNativePlatform() && request.headers) {
        request.headers['X-Client-Type'] = 'native';
        const isRefreshCall =
          typeof request.url === 'string' && request.url.includes('/auth/refresh');
        if (isRefreshCall) {
          const stored = await getStoredRefreshToken();
          if (stored) {
            request.headers['X-Refresh-Token'] = stored;
          }
        }
      }

      if (request.headers) {
        let subdomain: string | null = null;

        const urlParams = new URLSearchParams(window.location.search);
        const tenantParam = urlParams.get('tenant');
        if (tenantParam) {
          subdomain = tenantParam;
          localStorage.setItem('tenantSubdomain', tenantParam);
          sessionStorage.setItem('tenantSubdomain', tenantParam);
        }

        if (!subdomain) {
          const hostname = window.location.hostname;
          if (
            !hostname.includes('azurestaticapps.net') &&
            hostname !== 'localhost' &&
            !hostname.startsWith('app.') &&
            !hostname.startsWith('www.')
          ) {
            const parts = hostname.split('.');
            if (parts.length >= 3) {
              subdomain = parts[0];
              localStorage.setItem('tenantSubdomain', subdomain);
              sessionStorage.setItem('tenantSubdomain', subdomain);
            }
          }
        }

        if (!subdomain) {
          subdomain = getOAuthTenant();
        }

        if (!subdomain) {
          subdomain = sessionStorage.getItem('tenantSubdomain');
        }

        if (!subdomain) {
          subdomain = localStorage.getItem('tenantSubdomain');
          if (subdomain) {
            sessionStorage.setItem('tenantSubdomain', subdomain);
          }
        }

        if (subdomain && !config.isPublicRegistrationRoute()) {
          request.headers['X-Tenant-Subdomain'] = subdomain;
        }
      }

      return request;
    },
    (error) => Promise.reject(error),
  );

  let isRefreshing = false;
  let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  }> = [];

  const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token!);
      }
    });
    failedQueue = [];
  };

  const shouldRedirectAfterAuthFailure = (): boolean => {
    const currentPath = window.location.pathname;
    if (config.routes.publicPaths.includes(currentPath)) return false;
    if (config.routes.skipRedirectForPath?.(currentPath)) return false;
    return true;
  };

  const clearWorkboxCaches = () => {
    if ('caches' in window) {
      caches
        .keys()
        .then((cacheNames) => {
          cacheNames
            .filter((name) => name.includes('workbox') || name.includes('api-cache'))
            .forEach((name) => caches.delete(name));
        })
        .catch(() => {
          // Ignore cache clearing errors.
        });
    }
  };

  apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        // 401s from unauthenticated bootstrap endpoints (login, SSO sign-in,
        // MFA verify, refresh itself) mean "credentials are bad" — NOT
        // "session expired." Falling into the refresh path queues the
        // refresh request behind itself and the response chain never
        // resolves; the visible symptom is an indefinite spinner after a
        // bad login, which is what Apple's TestFlight reviewer hit when
        // tapping Sign in with Apple with their personal Apple ID (no PP
        // account). Let the caller's catch handle these directly.
        const url = typeof originalRequest.url === 'string' ? originalRequest.url : '';
        const isAuthBootstrapCall =
          url.includes('/auth/login') ||
          url.includes('/auth/refresh') ||
          url.includes('/auth/apple-sso') ||
          url.includes('/auth/google-sso') ||
          url.includes('/auth/mfa/');
        if (isAuthBootstrapCall) {
          return Promise.reject(error);
        }

        const errorCode = (error.response.data as { code?: string })?.code;
        if (errorCode === 'SESSION_TIMEOUT') {
          storage.remove(STORAGE_KEYS.JWT_TOKEN);
          clearWorkboxCaches();

          if (shouldRedirectAfterAuthFailure()) {
            config.notify.error(
              'Your session has expired due to inactivity. Please log in again.',
            );
            const from = encodeURIComponent(
              window.location.pathname + window.location.search,
            );
            window.location.href = `${config.routes.login}?reason=session-timeout&from=${from}`;
          }

          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = 'Bearer ' + token;
              }
              return apiClient(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await apiClient.post('/auth/refresh', {});
          const newAccessToken = response.data.accessToken;
          const newRefreshToken = response.data.refreshToken as string | null | undefined;

          storage.set(STORAGE_KEYS.JWT_TOKEN, newAccessToken);
          await storeRefreshTokenIfNative(newRefreshToken);

          processQueue(null, newAccessToken);

          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
          }
          return apiClient(originalRequest);
        } catch (refreshError) {
          processQueue(
            refreshError instanceof Error
              ? refreshError
              : new Error('Token refresh failed'),
            null,
          );
          storage.remove(STORAGE_KEYS.JWT_TOKEN);
          clearWorkboxCaches();

          if (shouldRedirectAfterAuthFailure()) {
            const from = encodeURIComponent(
              window.location.pathname + window.location.search,
            );
            window.location.href = `${config.routes.login}?reason=session-expired&from=${from}`;
            config.notify.error('Your session has expired. Please login again.');
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Tenant-not-found redirect. Web only — capacitor:// shells have no
      // tenant URL to recover toward, so let the caller handle the error.
      if (
        error.response?.status === 404 &&
        error.response.data?.code === 'TENANT_NOT_FOUND' &&
        !config.isPublicRegistrationRoute() &&
        !Capacitor.isNativePlatform()
      ) {
        window.location.href = config.routes.tenantNotFound;
      }

      if (error.response?.status === 403) {
        const data = error.response.data as { code?: string; module?: string; message?: string } | undefined;
        if (data?.code === 'module_not_included' && config.onModuleNotIncluded) {
          config.onModuleNotIncluded({
            module: data.module || '',
            message: data.message || 'This feature is not included in your plan.',
          });
        } else {
          console.error('Access forbidden:', data?.message);
        }
      }

      if (error.response?.status === 400 && config.onLimitExceeded) {
        const data = error.response.data as {
          code?: string;
          limitType?: string;
          error?: string;
        };
        if (data?.code === 'LIMIT_EXCEEDED') {
          config.onLimitExceeded({
            limitType: data.limitType || 'default',
            message: data.error || 'You have reached a limit on your current plan.',
          });
        }
      }

      return Promise.reject(error);
    },
  );

  return apiClient;
}
